import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useUserRoleContext } from './UserRoleContext';

export interface PagePermissionsContextValue {
  loading: boolean;
  isFounder: boolean;
  // Whether the Operations Hub's Team > "Portal access matrix" has this
  // user checked for the 'rxk' portal — the actual front-door gate (see
  // RequirePagePermission). rxk_role_permissions/allowedPages below only
  // ever narrows which PAGES someone already let in this way can see —
  // it was never meant to also decide whether they get in at all.
  hasPortalAccess: boolean;
  allowedPages: Set<string>;
}

const PagePermissionsContext = createContext<PagePermissionsContextValue | undefined>(undefined);

// Same "mount once at app root, react to role changes" pattern as
// UserRoleContext — every admin route is a separate top-level React Router
// route, so a per-component hook here would re-fetch (and briefly flash an
// empty/full sidebar) on every single admin navigation.
export function PagePermissionsProvider({ children }: { children: ReactNode }) {
  const { role, user, loading: roleLoading } = useUserRoleContext();
  const [loading, setLoading] = useState(true);
  const [isFounder, setIsFounder] = useState(false);
  const [hasPortalAccess, setHasPortalAccess] = useState(false);
  const [allowedPages, setAllowedPages] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (roleLoading) return;
    if (!user) {
      setIsFounder(false);
      setHasPortalAccess(false);
      setAllowedPages(new Set());
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);

    Promise.all([
      supabase.rpc('is_founder'),
      supabase.from('portal_access').select('portal_key').eq('user_id', user.id).eq('portal_key', 'rxk').maybeSingle(),
      role
        ? supabase.from('rxk_role_permissions').select('page_key').eq('role', role)
        : Promise.resolve({ data: [] as { page_key: string }[] }),
    ]).then(([founderRes, portalRes, permsRes]) => {
      if (!mounted) return;
      setIsFounder(!!founderRes.data);
      setHasPortalAccess(!!portalRes.data);
      setAllowedPages(new Set((permsRes.data ?? []).map((r) => r.page_key)));
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
    // user?.id below (not the user object) on purpose — supabase-js hands
    // back a brand-new User object on every onAuthStateChange event,
    // including a silent TOKEN_REFRESHED it fires whenever a background tab
    // regains focus. With the object itself in this array, that alone
    // re-ran this effect, calling setLoading(true) for no real reason —
    // which flipped RequirePagePermission's guard to its <Spinner/> branch,
    // unmounting the entire routed page (and anything open inside it, e.g.
    // NewOrderModal mid-scan) just from switching browser tabs and back.
    // user?.id only changes on an actual sign-in/out, the only time
    // permissions genuinely need re-checking.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, user?.id, roleLoading]);

  return (
    <PagePermissionsContext.Provider value={{ loading: roleLoading || loading, isFounder, hasPortalAccess, allowedPages }}>
      {children}
    </PagePermissionsContext.Provider>
  );
}

export function usePagePermissions(): PagePermissionsContextValue {
  const ctx = useContext(PagePermissionsContext);
  if (!ctx) throw new Error('usePagePermissions must be used within <PagePermissionsProvider>');
  return ctx;
}
